import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  List, AlertTriangle, CheckCircle2, Search, Download, Upload,
  FileText, Building2, Workflow, BarChart2, Clock, Paperclip, BookOpen,
} from 'lucide-react';

import { AccountsImportPanel }  from '@/components/gl/AccountsImportPanel';
import { MasterToolbar }        from '@/components/master/MasterToolbar';
import { MasterSearchModal }    from '@/components/master/MasterSearchModal';
import type { SearchColumn }    from '@/components/master/MasterSearchModal';
import { MasterReportPreview }  from '@/components/master/report/MasterReportPreview';
import { ReportHeader }         from '@/components/master/report/ReportHeader';
import { ReportSection }        from '@/components/master/report/ReportSection';
import { ReportFooter }         from '@/components/master/report/ReportFooter';
import { ConfirmDialog }        from '@/components/ui/ConfirmDialog';
import { Input }                from '@/components/ui/Input';
import { Combobox }             from '@/components/ui/Combobox';
import { Badge }                from '@/components/ui/Badge';
import { Spinner }              from '@/components/ui/Spinner';

import { AccountTreePanel }     from '@/components/erp/AccountTreePanel';
import { AccountLookupField }   from '@/components/erp/AccountLookupField';

import { useAuth }              from '@/hooks/useAuth';
import { usePermission }        from '@/hooks/usePermission';
import { useMasterScreenState } from '@/hooks/useMasterScreenState';
import { ScreenModeBadge } from '@/components/master/ScreenModeBadge';
import { Permissions }          from '@/constants/permissions';
import { AppRoutes }            from '@/constants/routes';
import { accountApi }           from '@/api/account.api';
import { locationApi }          from '@/api/location.api';
import type {
  AccountDto, AccountListDto, AccountTreeNodeDto,
  AccountType, AccountNature, CurrencyControl, CashFlowCategory, FinancialStatementGroup,
} from '@/types/account.types';
import { ACCOUNT_CATEGORIES, ACCOUNT_SUBCATEGORIES } from '@/types/account.types';
import { CROSSING_OPTIONS } from '@/constants/chequeCrossing';
import { cn } from '@/utils/cn';

// ── Constants ─────────────────────────────────────────────────────────────────

const ACCOUNT_TYPES: AccountType[] = ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'];

const TYPE_BADGE: Record<AccountType, string> = {
  Asset:     'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  Liability: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  Equity:    'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  Revenue:   'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  Expense:   'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
};

const TABS = [
  { key: 'general',    label: 'General',               icon: FileText },
  { key: 'financial',  label: 'Financial Controls',    icon: BarChart2 },
  { key: 'org',        label: 'Org. Rules',            icon: Building2 },
  { key: 'posting',    label: 'Posting & Workflow',    icon: Workflow },
  { key: 'reporting',  label: 'Reporting',             icon: Download },
  { key: 'audit',      label: 'Audit',                 icon: Clock },
  { key: 'attachments',label: 'Attachments',           icon: Paperclip },
] as const;
type Tab = (typeof TABS)[number]['key'];

// ── Zod schema ─────────────────────────────────────────────────────────────────

const schema = z.object({
  // Tab 1 - General
  code:             z.string().min(1, 'Code is required').max(20),
  name:             z.string().min(1, 'Name is required').max(200),
  shortName:        z.string().max(30).nullable().optional(),
  description:      z.string().max(500).nullable().optional(),
  accountType:      z.enum(['Asset', 'Liability', 'Equity', 'Revenue', 'Expense']),
  accountNature:    z.enum(['Debit', 'Credit']),
  accountCategory:  z.string().nullable().optional(),
  accountSubcategory: z.string().nullable().optional(),
  parentId:         z.string().uuid().nullable().optional(),
  isPostingAccount: z.boolean(),
  isControlAccount: z.boolean(),
  isActive:         z.boolean(),

  // Tab 2 - Financial Controls
  currencyControl:         z.enum(['ALL', 'SINGLE', 'BASE_ONLY']).nullable().optional(),
  allowMultiCurrency:      z.boolean(),
  taxApplicable:           z.boolean(),
  cashFlowCategory:        z.enum(['OPERATING', 'INVESTING', 'FINANCING', 'NONE']).nullable().optional(),
  financialStatementGroup: z.enum(['BALANCE_SHEET', 'INCOME_STATEMENT']).nullable().optional(),
  revaluationRequired:     z.boolean(),
  isRetainedEarnings:      z.boolean(),

  // Tab 3 - Organisational Rules
  costCenterMandatory: z.boolean(),
  locationMandatory:   z.boolean(),
  projectMandatory:    z.boolean(),
  departmentMandatory: z.boolean(),

  // Tab 4 - Posting & Workflow
  allowManualJournal:    z.boolean(),
  allowPaymentPosting:   z.boolean(),
  allowReceiptPosting:   z.boolean(),
  approvalRequired:      z.boolean(),
  postingRestriction:    z.string().max(100).nullable().optional(),
  transactionRestriction: z.string().max(100).nullable().optional(),

  // Tab 5 - Reporting & Integration
  reportingGroup:       z.string().max(50).nullable().optional(),
  reportingSequence:    z.number().int().positive().nullable().optional(),
  externalMappingCode:  z.string().max(50).nullable().optional(),
  consolidationGroup:   z.string().max(50).nullable().optional(),
  ifrsMappingCode:      z.string().max(50).nullable().optional(),
  taxReportMappingCode: z.string().max(50).nullable().optional(),
  dashboardGroup:       z.string().max(50).nullable().optional(),
  chequePayeeName:      z.string().max(255).nullable().optional(),
  defaultChequeCrossing: z.string().nullable().optional(),
});

type FormValues = z.infer<typeof schema>;

// ── Defaults ──────────────────────────────────────────────────────────────────

function makeDefaults(code = ''): FormValues {
  return {
    code, name: '', shortName: null, description: null,
    accountType: 'Asset', accountNature: 'Debit',
    accountCategory: null, accountSubcategory: null,
    parentId: null, isPostingAccount: true, isControlAccount: false, isActive: true,
    currencyControl: null, allowMultiCurrency: true, taxApplicable: false,
    cashFlowCategory: null, financialStatementGroup: 'BALANCE_SHEET',
    revaluationRequired: false, isRetainedEarnings: false,
    costCenterMandatory: false, locationMandatory: false,
    projectMandatory: false, departmentMandatory: false,
    allowManualJournal: true, allowPaymentPosting: true,
    allowReceiptPosting: true, approvalRequired: false,
    postingRestriction: null, transactionRestriction: null,
    reportingGroup: null, reportingSequence: null,
    externalMappingCode: null, consolidationGroup: null,
    ifrsMappingCode: null, taxReportMappingCode: null, dashboardGroup: null,
    chequePayeeName: null,
    defaultChequeCrossing: null,
  };
}

function deriveNature(type: AccountType): AccountNature {
  return (type === 'Asset' || type === 'Expense') ? 'Debit' : 'Credit';
}

function deriveFinancialGroup(type: AccountType): FinancialStatementGroup {
  return (type === 'Asset' || type === 'Liability' || type === 'Equity')
    ? 'BALANCE_SHEET' : 'INCOME_STATEMENT';
}

function dtoToForm(dto: AccountDto): FormValues {
  return {
    code: dto.code, name: dto.name,
    shortName: dto.shortName ?? null,
    description: dto.description ?? null,
    accountType: dto.accountType,
    accountNature: dto.accountNature,
    accountCategory: dto.accountCategory ?? null,
    accountSubcategory: dto.accountSubcategory ?? null,
    parentId: dto.parentId ?? null,
    isPostingAccount: dto.isPostingAccount,
    isControlAccount: dto.isControlAccount,
    isActive: dto.isActive,
    currencyControl: (dto.currencyControl as CurrencyControl) ?? null,
    allowMultiCurrency: dto.allowMultiCurrency,
    taxApplicable: dto.taxApplicable,
    cashFlowCategory: (dto.cashFlowCategory as CashFlowCategory) ?? null,
    financialStatementGroup: (dto.financialStatementGroup as FinancialStatementGroup) ?? null,
    revaluationRequired: dto.revaluationRequired,
    isRetainedEarnings: dto.isRetainedEarnings,
    costCenterMandatory: dto.costCenterMandatory,
    locationMandatory: dto.locationMandatory,
    projectMandatory: dto.projectMandatory,
    departmentMandatory: dto.departmentMandatory,
    allowManualJournal: dto.allowManualJournal,
    allowPaymentPosting: dto.allowPaymentPosting,
    allowReceiptPosting: dto.allowReceiptPosting,
    approvalRequired: dto.approvalRequired,
    postingRestriction: dto.postingRestriction ?? null,
    transactionRestriction: dto.transactionRestriction ?? null,
    reportingGroup: dto.reportingGroup ?? null,
    reportingSequence: dto.reportingSequence ?? null,
    externalMappingCode: dto.externalMappingCode ?? null,
    consolidationGroup: dto.consolidationGroup ?? null,
    ifrsMappingCode: dto.ifrsMappingCode ?? null,
    taxReportMappingCode: dto.taxReportMappingCode ?? null,
    dashboardGroup: dto.dashboardGroup ?? null,
    chequePayeeName: dto.chequePayeeName ?? null,
    defaultChequeCrossing: dto.defaultChequeCrossing ?? null,
  };
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// ── Search columns ─────────────────────────────────────────────────────────────

const SEARCH_COLUMNS: SearchColumn<AccountListDto>[] = [
  { key: 'code', label: 'Code', className: 'w-24 font-mono' },
  { key: 'name', label: 'Name' },
  { key: 'accountType', label: 'Type', className: 'w-24',
    render: r => (
      <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-semibold', TYPE_BADGE[r.accountType])}>
        {r.accountType}
      </span>
    ) },
  { key: 'parentCode', label: 'Parent', className: 'w-20 font-mono',
    render: r => r.parentCode ?? '—' },
  { key: 'isPostingAccount', label: 'Mode', className: 'w-16 text-center',
    render: r => <Badge variant={r.isPostingAccount ? 'default' : 'success'}>{r.isPostingAccount ? 'Post' : 'Hdr'}</Badge> },
  { key: 'isActive', label: 'Status', className: 'w-16',
    render: r => <Badge variant={r.isActive ? 'success' : 'default'}>{r.isActive ? 'Active' : 'Inactive'}</Badge> },
];

// ── Reusable field components ─────────────────────────────────────────────────

const FieldLabel = ({ children, hint }: { children: React.ReactNode; hint?: string }) => (
  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
    {children}
    {hint && <span className="ml-1 text-[10px] font-normal text-gray-400">{hint}</span>}
  </label>
);

const ToggleSwitch = ({
  label, description, checked, onChange, disabled,
}: {
  label: string; description?: string; checked: boolean;
  onChange: (v: boolean) => void; disabled?: boolean;
}) => (
  <label className={cn(
    'flex items-start gap-3 py-2',
    disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
  )}>
    <div className="relative mt-0.5 flex-shrink-0">
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        disabled={disabled}
        className="sr-only peer"
      />
      <div className="w-9 h-5 rounded-full bg-gray-200 dark:bg-gray-600 peer-checked:bg-brand-500 peer-disabled:opacity-50 transition-colors" />
      <div className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
    </div>
    <div>
      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{label}</span>
      {description && <p className="text-[10px] text-gray-400 mt-0.5">{description}</p>}
    </div>
  </label>
);

const SelectField = ({
  label, value, onChange, disabled, options, hint, placeholder,
}: {
  label?: string; value: string; onChange: (v: string) => void;
  disabled?: boolean; options: { value: string; label: string }[];
  hint?: string; placeholder?: string;
}) => (
  <div>
    {label && <FieldLabel hint={hint}>{label}</FieldLabel>}
    <Combobox
      value={value}
      onChange={onChange}
      disabled={disabled}
      options={options}
      placeholder={placeholder}
    />
  </div>
);

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-center gap-2 mb-3 pb-1.5 border-b border-gray-100 dark:border-gray-700">
    <h3 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
      {children}
    </h3>
  </div>
);

// ── Main component ─────────────────────────────────────────────────────────────

export const ChartOfAccountsPage = () => {
  const navigate = useNavigate();
  const { user }          = useAuth();
  const { hasPermission } = usePermission();

  const canView   = hasPermission(Permissions.GL_COA_VIEW);
  const canCreate = hasPermission(Permissions.GL_COA_CREATE);
  const canEdit   = hasPermission(Permissions.GL_COA_EDIT);
  const canDelete = hasPermission(Permissions.GL_COA_DELETE);
  const canPrint  = hasPermission(Permissions.GL_COA_PRINT);
  const canExport = hasPermission(Permissions.GL_COA_EXPORT);

  const screen = useMasterScreenState({ canEdit, canDelete });
  const codeInputRef = useRef<HTMLInputElement>(null);

  // ── State ─────────────────────────────────────────────────────────────────────
  const [importOpen,     setImportOpen]   = useState(false);
  const [record,         setRecord]       = useState<AccountDto | null>(null);
  const [codeInput,      setCodeInput]    = useState('');
  const [activeTab,      setActiveTab]    = useState<Tab>('general');
  const [searchOpen,     setSearchOpen]   = useState(false);
  const [saving,         setSaving]       = useState(false);
  const [lookupBusy,     setLookupBusy]   = useState(false);
  const [flashMsg,       setFlashMsg]     = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [printOpen,      setPrintOpen]    = useState(false);

  const [pendingSave,       setPendingSave]       = useState<FormValues | null>(null);
  const [showSaveConfirm,   setShowSaveConfirm]   = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showNewConfirm,    setShowNewConfirm]    = useState<string | null>(null);

  // Parent account display state (separate from form to show code+name)
  const [parentDisplay, setParentDisplay] = useState<{ id: string | null; code: string | null; name: string | null }>({
    id: null, code: null, name: null,
  });

  // ── Permitted locations (GAP-003) ───────────────────────────────────────────
  // Held outside the react-hook-form values because it is saved through its own endpoint:
  // changing who may use an account is a security change, audited separately from an edit to the
  // account's name or category.
  const [companyLocations,    setCompanyLocations]    = useState<{ id: string; code: string; name: string }[]>([]);
  const [permittedLocations,  setPermittedLocations]  = useState<string[]>([]);
  const [locationsBaseline,   setLocationsBaseline]   = useState<string[]>([]);

  // Tree state
  const [treeNodes,    setTreeNodes]    = useState<AccountTreeNodeDto[]>([]);
  const [treeLoading,  setTreeLoading]  = useState(false);
  const [treeSelectedId, setTreeSelectedId] = useState<string | null>(null);

  // ── Form ──────────────────────────────────────────────────────────────────────
  const {
    register, handleSubmit, control, reset, watch, setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: makeDefaults(),
  });

  const watchedType    = watch('accountType');
  const isActive       = watch('isActive');
  const isPosting      = watch('isPostingAccount');
  const approvalReq    = watch('approvalRequired');
  const fieldsDisabled = screen.isInitial || screen.isView;

  // ── Tree loading ──────────────────────────────────────────────────────────────
  const loadTree = useCallback(async () => {
    if (!canView) return;
    setTreeLoading(true);
    try {
      const res = await accountApi.getTree();
      setTreeNodes(res.data.data ?? []);
    } catch {
      // Tree is optional — don't flash error on load
    } finally {
      setTreeLoading(false);
    }
  }, [canView]);

  useEffect(() => { loadTree(); }, [loadTree]);

  // Every location in the company, not just the ones this user works at: this is the screen where
  // the restriction is maintained, so a head-office administrator has to be able to grant a branch
  // they do not themselves post from.
  useEffect(() => {
    if (!canView) return;
    locationApi.getList({ pageNumber: 1, pageSize: 500, isActive: true })
      .then(res => setCompanyLocations(
        (res.data.data?.items ?? []).map(l => ({ id: l.id, code: l.code, name: l.name }))))
      .catch(() => { /* the section simply shows nothing to grant */ });
  }, [canView]);

  // F9 — jump to this account's ledger listing (422175). There is no existing shortcut-key
  // convention anywhere in the app to follow, so this establishes the first one: F9 is free in
  // every major browser (no default binding) and is the traditional "view ledger/detail" key in
  // ERP software. Suppressed while the search modal is open, since F9 there would be ambiguous —
  // jump to the loaded account, or the one being searched for?
  //
  // Registered on the CAPTURE phase, not bubble: the code-lookup field still has focus right
  // after typing a code and pressing Enter to load it — the natural moment to then press F9 —
  // and capture is what lets this shortcut win regardless of whatever that focused field would
  // otherwise do with the keystroke, rather than depending on it not minding an unknown key.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'F9' || !record || searchOpen) return;
      e.preventDefault();
      navigate(`${AppRoutes.REPORTS_ACCOUNT_LEDGER}?accountId=${record.id}`);
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [record, searchOpen, navigate]);

  // ── Helpers ───────────────────────────────────────────────────────────────────
  const flash = (type: 'success' | 'error', text: string) => {
    setFlashMsg({ type, text });
    setTimeout(() => setFlashMsg(null), 5000);
  };

  const resetToInitial = useCallback(() => {
    setRecord(null);
    setCodeInput('');
    reset(makeDefaults());
    setParentDisplay({ id: null, code: null, name: null });
    screen.setMode('initial');
    setActiveTab('general');
    setPendingSave(null);
    setTreeSelectedId(null);
    setTimeout(() => codeInputRef.current?.focus(), 50);
  }, [reset, screen]);

  const loadRecord = useCallback((dto: AccountDto) => {
    setRecord(dto);
    setPermittedLocations([]);
    setLocationsBaseline([]);
    accountApi.getLocations(dto.id)
      .then(res => {
        const ids = (res.data.data?.locations ?? []).map(l => l.locationId);
        setPermittedLocations(ids);
        setLocationsBaseline(ids);
      })
      .catch(() => { /* leave it showing "all locations" rather than a wrong restriction */ });
    reset(dtoToForm(dto));
    setCodeInput(dto.code);
    setParentDisplay({ id: dto.parentId ?? null, code: dto.parentCode ?? null, name: dto.parentName ?? null });
    screen.setMode('view');
    setActiveTab('general');
    setTreeSelectedId(dto.id);
  }, [reset, screen]);

  // Auto-derive nature + financial group when type changes
  const handleTypeChange = (newType: AccountType) => {
    setValue('accountType', newType);
    setValue('accountNature', deriveNature(newType));
    setValue('financialStatementGroup', deriveFinancialGroup(newType));
    setValue('accountCategory', null);  // reset category when type changes
  };

  // ── Code lookup ───────────────────────────────────────────────────────────────
  const handleCodeLookup = async () => {
    const code = codeInput.trim().toUpperCase();
    if (!code) return;
    if (!canView) { flash('error', 'You do not have permission to view accounts.'); return; }
    setLookupBusy(true);
    try {
      const res = await accountApi.getByCode(code);
      if (res.data.data) loadRecord(res.data.data);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 404) {
        if (!canCreate) { flash('error', `Account code '${code}' was not found.`); return; }
        setShowNewConfirm(code);
      } else {
        flash('error', 'Lookup failed. Please try again.');
      }
    } finally {
      setLookupBusy(false);
    }
  };

  const handleConfirmCreateNew = () => {
    const code = showNewConfirm;
    setShowNewConfirm(null);
    if (!code) return;
    reset(makeDefaults(code));
    setCodeInput(code);
    setRecord(null);
    setPermittedLocations([]);
    setLocationsBaseline([]);
    setParentDisplay({ id: null, code: null, name: null });
    screen.setMode('new');
    setActiveTab('general');
  };

  // ── Tree selection ────────────────────────────────────────────────────────────
  const handleTreeSelect = (node: AccountTreeNodeDto) => {
    if (screen.isNew || screen.isEdit) return; // don't navigate away while editing
    setTreeSelectedId(node.id);
    setLookupBusy(true);
    accountApi.getById(node.id)
      .then(res => { if (res.data.data) loadRecord(res.data.data); })
      .catch(() => flash('error', 'Failed to load account.'))
      .finally(() => setLookupBusy(false));
  };

  // ── Toolbar ───────────────────────────────────────────────────────────────────
  const handleRefresh = () => { resetToInitial(); loadTree(); };
  const handleEdit    = () => screen.setMode('edit');
  const handleSaveClick = () => {
    handleSubmit((validData) => {
      setPendingSave(validData);
      setShowSaveConfirm(true);
    })();
  };

  const executeSave = async () => {
    if (!pendingSave) return;
    setShowSaveConfirm(false);
    setSaving(true);
    try {
      const d = pendingSave;
      const nullify = (v: string | null | undefined) => v?.trim() || null;
      const payload = {
        code:             d.code.trim().toUpperCase(),
        name:             d.name.trim(),
        accountType:      d.accountType,
        accountNature:    d.accountNature,
        isPostingAccount: d.isPostingAccount,
        parentId:         parentDisplay.id,
        description:      nullify(d.description),
        shortName:        nullify(d.shortName),
        accountCategory:  nullify(d.accountCategory),
        accountSubcategory: nullify(d.accountSubcategory),
        isControlAccount: d.isControlAccount,
        currencyControl:  d.currencyControl || null,
        allowMultiCurrency: d.allowMultiCurrency,
        taxApplicable:    d.taxApplicable,
        cashFlowCategory: d.cashFlowCategory || null,
        financialStatementGroup: d.financialStatementGroup || null,
        revaluationRequired: d.revaluationRequired,
        isRetainedEarnings: d.isRetainedEarnings,
        costCenterMandatory: d.costCenterMandatory,
        locationMandatory: d.locationMandatory,
        projectMandatory: d.projectMandatory,
        departmentMandatory: d.departmentMandatory,
        allowManualJournal: d.allowManualJournal,
        allowPaymentPosting: d.allowPaymentPosting,
        allowReceiptPosting: d.allowReceiptPosting,
        approvalRequired: d.approvalRequired,
        postingRestriction: nullify(d.postingRestriction),
        transactionRestriction: nullify(d.transactionRestriction),
        reportingGroup: nullify(d.reportingGroup),
        reportingSequence: d.reportingSequence ?? null,
        externalMappingCode: nullify(d.externalMappingCode),
        consolidationGroup: nullify(d.consolidationGroup),
        ifrsMappingCode: nullify(d.ifrsMappingCode),
        taxReportMappingCode: nullify(d.taxReportMappingCode),
        dashboardGroup: nullify(d.dashboardGroup),
        chequePayeeName: nullify(d.chequePayeeName),
        defaultChequeCrossing: nullify(d.defaultChequeCrossing),
      };

      // The location set goes through its own endpoint, and only when it actually changed —
      // an unchanged set must not write an audit record saying access was re-granted.
      const sameLocations =
        permittedLocations.length === locationsBaseline.length &&
        permittedLocations.every(id => locationsBaseline.includes(id));

      if (screen.isNew) {
        const created = await accountApi.create(payload);
        const newId = created.data.data?.id;
        if (newId && permittedLocations.length > 0) {
          await accountApi.setLocations(newId, permittedLocations);
        }
        flash('success', 'Account created successfully.');
      } else if (screen.isEdit && record) {
        await accountApi.update(record.id, { ...payload, isActive: d.isActive });
        if (!sameLocations) {
          await accountApi.setLocations(record.id, permittedLocations);
        }
        flash('success', 'Account updated successfully.');
      }
      await loadTree();
      resetToInitial();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })
        .response?.data?.message ?? 'Save failed. Please try again.';
      flash('error', msg);
    } finally {
      setSaving(false);
      setPendingSave(null);
    }
  };

  const handleDelete = () => setShowDeleteConfirm(true);
  const executeDelete = async () => {
    if (!record) return;
    setShowDeleteConfirm(false);
    setSaving(true);
    try {
      await accountApi.remove(record.id);
      flash('success', `Account '${record.code} – ${record.name}' deleted.`);
      await loadTree();
      resetToInitial();
    } catch {
      flash('error', 'Delete failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => setPrintOpen(true);
  const handleExit  = () => navigate(AppRoutes.DASHBOARD);

  // ── Search modal ──────────────────────────────────────────────────────────────
  const fetchAccounts = useCallback(async (search: string, page: number, pageSize: number) => {
    const res = await accountApi.getList({ search, pageNumber: page, pageSize });
    const d = res.data.data;
    return { items: d?.items ?? [], totalCount: d?.totalCount ?? 0 };
  }, []);

  const handleSearchSelect = (item: AccountListDto) => {
    setSearchOpen(false);
    setLookupBusy(true);
    accountApi.getById(item.id)
      .then(res => { if (res.data.data) loadRecord(res.data.data); })
      .catch(() => flash('error', 'Failed to load account.'))
      .finally(() => setLookupBusy(false));
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full min-h-0">

      {/* ── Page header ── */}
      <div className="flex-shrink-0 flex items-center gap-3 px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <List size={16} className="text-brand-500 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h1 className="text-sm font-bold text-gray-900 dark:text-gray-100 leading-tight">
            Chart of Accounts
          </h1>
          <p className="text-[10px] text-gray-500 dark:text-gray-400">
            General Ledger · Master File · Tree View
          </p>
        </div>

        {/* Status badge */}
        <ScreenModeBadge isNew={screen.isNew} isEdit={screen.isEdit} isView={screen.isView} />

        {/* Ledger listing shortcut (422175) — F9, or click; only once an account is loaded */}
        {record && (
          <button
            type="button"
            title="View this account's ledger listing (F9)"
            onClick={() => navigate(`${AppRoutes.REPORTS_ACCOUNT_LEDGER}?accountId=${record.id}`)}
            className="flex items-center gap-1.5 h-7 px-2.5 rounded text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white transition-colors"
          >
            <BookOpen size={12} />
            <span className="hidden sm:inline">Ledger</span>
            <span className="hidden md:inline text-indigo-200 font-normal">F9</span>
          </button>
        )}

        {/* Import button (permission-gated) */}
        {canCreate && (
          <button
            type="button"
            title="Import from Excel"
            onClick={() => setImportOpen(v => !v)}
            className="flex items-center gap-1.5 h-7 px-2.5 rounded text-xs font-semibold bg-sky-600 hover:bg-sky-700 text-white transition-colors"
          >
            <Upload size={12} />
            <span className="hidden sm:inline">Import</span>
          </button>
        )}

        {/* Export button (permission-gated) */}
        {canExport && (
          <button
            type="button"
            title="Export to Excel"
            className="flex items-center gap-1.5 h-7 px-2.5 rounded text-xs font-semibold bg-teal-600 hover:bg-teal-700 text-white transition-colors"
          >
            <Download size={12} />
            <span className="hidden sm:inline">Export</span>
          </button>
        )}
      </div>

      {/* ── Import panel ── */}
      {importOpen && (
        <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <AccountsImportPanel
            onClose={() => setImportOpen(false)}
            onImported={handleRefresh}
          />
        </div>
      )}

      {/* ── Toolbar ── */}
      <MasterToolbar
        {...screen.toolbar}
        saving={saving}
        loading={lookupBusy}
        onSave={handleSaveClick}
        onEdit={handleEdit}
        onPrint={canPrint ? handlePrint : undefined}
        printEnabled={canPrint && (screen.isView || screen.isInitial)}
        onDelete={handleDelete}
        onRefresh={handleRefresh}
        onExit={handleExit}
      />

      {/* ── Flash message ── */}
      {flashMsg && (
        <div className={cn(
          'flex-shrink-0 flex items-center gap-2 px-4 py-2 text-xs',
          flashMsg.type === 'success'
            ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-b border-emerald-200 dark:border-emerald-800'
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-b border-red-200 dark:border-red-800',
        )}>
          {flashMsg.type === 'success'
            ? <CheckCircle2 size={13} className="flex-shrink-0" />
            : <AlertTriangle size={13} className="flex-shrink-0" />}
          {flashMsg.text}
        </div>
      )}

      {/* ── Code lookup bar ── */}
      <div className="flex-shrink-0 flex items-center gap-3 px-4 py-1.5 bg-gray-50 dark:bg-gray-800/40 border-b border-gray-200 dark:border-gray-700">
        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide w-10 flex-shrink-0">Code</span>
        <div className="flex items-center gap-1.5">
          <input
            ref={codeInputRef}
            type="text"
            value={codeInput}
            onChange={e => setCodeInput(e.target.value.toUpperCase())}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleCodeLookup(); } }}
            disabled={screen.isNew || screen.isEdit || saving}
            placeholder="Enter code + ↵"
            className={cn(
              'w-40 h-7 px-2.5 text-xs font-mono rounded border',
              'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
              'placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-brand-500',
              'disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed',
              'border-gray-300 dark:border-gray-600 uppercase',
            )}
          />
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            disabled={screen.isNew || screen.isEdit || saving}
            className={cn(
              'h-7 w-7 flex items-center justify-center rounded border',
              'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800',
              'text-gray-500 dark:text-gray-400 hover:text-brand-600 hover:border-brand-400',
              'disabled:opacity-40 disabled:cursor-not-allowed transition-colors',
            )}
            title="Browse accounts"
          >
            {lookupBusy ? <Spinner size="sm" /> : <Search size={12} />}
          </button>
        </div>

        {record && (
          <div className="flex items-center gap-2 ml-2">
            <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-semibold', TYPE_BADGE[record.accountType])}>
              {record.accountType}
            </span>
            <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate max-w-xs">
              {record.name}
            </span>
            {!record.isPostingAccount && (
              <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-[10px] font-bold">
                HEADER
              </span>
            )}
            {record.isControlAccount && (
              <span className="px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-[10px] font-bold">
                CTRL
              </span>
            )}
            {!record.isActive && (
              <span className="px-1.5 py-0.5 rounded bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 text-[10px] font-bold">
                INACTIVE
              </span>
            )}
          </div>
        )}

        {screen.isInitial && (
          <span className="text-[10px] text-gray-400 ml-2 italic">
            Enter an account code + Enter, click the tree, or use the search icon.
          </span>
        )}
      </div>

      {/* ── TREE_FORM layout ── */}
      <div className="flex flex-1 min-h-0 overflow-hidden">

        {/* LEFT: Account hierarchy tree (fixed width) */}
        <div className="w-72 flex-shrink-0 overflow-hidden">
          <AccountTreePanel
            nodes={treeNodes}
            loading={treeLoading}
            selectedId={treeSelectedId}
            onSelect={handleTreeSelect}
            onRefresh={loadTree}
          />
        </div>

        {/* RIGHT: Tabbed maintenance form */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

          {/* Tabs */}
          <div className="flex-shrink-0 flex border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-x-auto">
            {TABS.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-2 text-[11px] font-medium border-b-2 whitespace-nowrap transition-colors',
                    activeTab === tab.key
                      ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200',
                  )}
                >
                  <Icon size={12} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-y-auto">
            <form onSubmit={e => e.preventDefault()} noValidate>
              <div className="p-4 space-y-4 max-w-3xl">

                {/* ═══ TAB 1: GENERAL ════════════════════════════════════════════ */}
                {activeTab === 'general' && (
                  <>
                    <SectionHeading>Account Identification</SectionHeading>
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        label="Account Code *"
                        {...register('code')}
                        disabled
                        placeholder="— enter via code field above —"
                        className="font-mono uppercase text-xs"
                        hint={screen.isNew ? 'Code locked after entry.' : undefined}
                      />
                      <div>
                        <FieldLabel>Short Name</FieldLabel>
                        <input
                          {...register('shortName')}
                          disabled={fieldsDisabled}
                          placeholder="Abbreviated name"
                          maxLength={30}
                          className={cn(
                            'w-full h-8 px-2.5 rounded-md border text-xs',
                            'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
                            'border-gray-300 dark:border-gray-600',
                            'focus:outline-none focus:ring-1 focus:ring-brand-500',
                            'disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed',
                          )}
                        />
                      </div>
                      <Input
                        label="Account Name *"
                        {...register('name')}
                        error={errors.name?.message}
                        disabled={fieldsDisabled}
                        placeholder="e.g. Cash at Bank"
                        className="text-xs col-span-2"
                      />
                      <div className="col-span-2">
                        <FieldLabel>Description</FieldLabel>
                        <textarea
                          {...register('description')}
                          disabled={fieldsDisabled}
                          placeholder="Optional notes about this account"
                          rows={2}
                          className={cn(
                            'w-full px-2.5 py-2 rounded-md border text-xs resize-none',
                            'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
                            'border-gray-300 dark:border-gray-600',
                            'focus:outline-none focus:ring-1 focus:ring-brand-500',
                            'disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed',
                          )}
                        />
                      </div>
                    </div>

                    <SectionHeading>Classification</SectionHeading>
                    <div className="grid grid-cols-2 gap-3">
                      {/* Account Type */}
                      <div>
                        <FieldLabel>Account Type *</FieldLabel>
                        <Controller
                          name="accountType"
                          control={control}
                          render={({ field }) => (
                            <Combobox
                              value={field.value}
                              onChange={v => handleTypeChange(v as AccountType)}
                              disabled={fieldsDisabled}
                              clearable={false}
                              options={ACCOUNT_TYPES.map(t => ({ value: t, label: t }))}
                            />
                          )}
                        />
                        {watchedType && (
                          <span className={cn('mt-1 inline-block px-2 py-0.5 rounded text-[10px] font-semibold', TYPE_BADGE[watchedType])}>
                            {watchedType} Account
                          </span>
                        )}
                      </div>

                      {/* Normal Balance */}
                      <div>
                        <FieldLabel hint="(from Account Type)">Normal Balance</FieldLabel>
                        {/* Read-only: it follows from the type, and nothing in the system reads the
                            stored value anyway, so an override changed no behaviour — it only made
                            the screen disagree with the accounting. */}
                        <Controller name="accountNature" control={control}
                          render={({ field }) => (
                            <Combobox
                              value={field.value}
                              onChange={field.onChange}
                              disabled
                              clearable={false}
                              options={[
                                { value: 'Debit',  label: 'Debit'  },
                                { value: 'Credit', label: 'Credit' },
                              ]}
                            />
                          )}
                        />
                      </div>

                      {/* Account Category */}
                      <Controller
                        name="accountCategory"
                        control={control}
                        render={({ field }) => (
                          <SelectField
                            label="Account Category"
                            value={field.value ?? ''}
                            onChange={v => field.onChange(v || null)}
                            disabled={fieldsDisabled}
                            placeholder="— select category —"
                            options={(ACCOUNT_CATEGORIES[watchedType] ?? []).map(c => ({ value: c, label: c }))}
                          />
                        )}
                      />

                      {/* Account Subcategory — routing, not description.
                          This was a free text box, but the matching is exact and case-sensitive:
                          "cash" or "CASH" silently routed nothing, and the account simply never
                          appeared in Pay From or on the Cash Book with nothing said. */}
                      <Controller
                        name="accountSubcategory"
                        control={control}
                        render={({ field }) => (
                          <SelectField
                            label="Subcategory"
                            value={field.value ?? ''}
                            onChange={v => field.onChange(v || null)}
                            disabled={fieldsDisabled}
                            placeholder="— none —"
                            options={ACCOUNT_SUBCATEGORIES.map(s => ({ value: s.value, label: s.label }))}
                          />
                        )}
                      />
                    </div>

                    <SectionHeading>Hierarchy & Posting Rules</SectionHeading>

                    {/* Parent Account */}
                    <AccountLookupField
                      label="Parent Account"
                      value={parentDisplay.id}
                      displayCode={parentDisplay.code}
                      displayName={parentDisplay.name}
                      onChange={(id, code, name) => setParentDisplay({ id, code, name })}
                      disabled={fieldsDisabled}
                    />

                    <div className="grid grid-cols-2 gap-4 pt-1">
                      <Controller
                        name="isPostingAccount"
                        control={control}
                        render={({ field }) => (
                          <ToggleSwitch
                            label={isPosting ? 'Posting Account' : 'Header / Group Account'}
                            description={isPosting
                              ? 'Journals can post directly to this account.'
                              : 'For sub-totalling only — journals cannot post here.'}
                            checked={field.value}
                            onChange={field.onChange}
                            disabled={fieldsDisabled}
                          />
                        )}
                      />
                      <Controller
                        name="isControlAccount"
                        control={control}
                        render={({ field }) => (
                          <ToggleSwitch
                            label="Control Account"
                            description="Linked to AR/AP sub-ledger. Manual journals blocked."
                            checked={field.value}
                            onChange={field.onChange}
                            disabled={fieldsDisabled}
                          />
                        )}
                      />
                    </div>

                    {/* Status — only in EDIT/VIEW */}
                    {(screen.isEdit || screen.isView) && (
                      <>
                        <SectionHeading>Status</SectionHeading>
                        <Controller
                          name="isActive"
                          control={control}
                          render={({ field }) => (
                            <ToggleSwitch
                              label={isActive ? 'Active' : 'Inactive'}
                              description="Inactive accounts are hidden from journal entry lookup."
                              checked={field.value}
                              onChange={field.onChange}
                              disabled={fieldsDisabled}
                            />
                          )}
                        />
                      </>
                    )}
                  </>
                )}

                {/* ═══ TAB 2: FINANCIAL CONTROLS ════════════════════════════════ */}
                {activeTab === 'financial' && (
                  <>
                    <SectionHeading>Currency & Tax</SectionHeading>
                    <div className="grid grid-cols-2 gap-3">
                      <Controller
                        name="currencyControl"
                        control={control}
                        render={({ field }) => (
                          <SelectField
                            label="Currency Control"
                            value={field.value ?? ''}
                            onChange={v => field.onChange(v || null)}
                            disabled={fieldsDisabled}
                            placeholder="— no restriction —"
                            options={[
                              { value: 'ALL',       label: 'All Currencies' },
                              { value: 'SINGLE',    label: 'Single Currency' },
                              { value: 'BASE_ONLY', label: 'Base Currency Only' },
                            ]}
                          />
                        )}
                      />
                      <Controller
                        name="cashFlowCategory"
                        control={control}
                        render={({ field }) => (
                          <SelectField
                            label="Cash Flow Category"
                            hint="(IAS 7)"
                            value={field.value ?? ''}
                            onChange={v => field.onChange(v || null)}
                            disabled={fieldsDisabled}
                            placeholder="— not classified —"
                            options={[
                              { value: 'OPERATING',  label: 'Operating Activities' },
                              { value: 'INVESTING',  label: 'Investing Activities' },
                              { value: 'FINANCING',  label: 'Financing Activities' },
                              { value: 'NONE',       label: 'Not Applicable' },
                            ]}
                          />
                        )}
                      />
                      <Controller
                        name="financialStatementGroup"
                        control={control}
                        render={({ field }) => (
                          <SelectField
                            label="Financial Statement"
                            value={field.value ?? ''}
                            onChange={v => field.onChange(v || null)}
                            disabled={fieldsDisabled}
                            placeholder="— select —"
                            options={[
                              { value: 'BALANCE_SHEET',    label: 'Balance Sheet' },
                              { value: 'INCOME_STATEMENT', label: 'Income Statement (P&L)' },
                            ]}
                          />
                        )}
                      />
                    </div>

                    <SectionHeading>Financial Flags</SectionHeading>
                    <div className="grid grid-cols-2 gap-2">
                      {([
                        ['allowMultiCurrency',  'Allow Multi-Currency',   'Accept transactions in foreign currencies.'],
                        ['taxApplicable',       'Tax Applicable',         'This account is subject to GST/VAT taxation.'],
                        ['revaluationRequired', 'Revaluation Required',   'Requires foreign currency revaluation at period-end.'],
                        ['isRetainedEarnings',  'Retained Earnings',      'P&L balance transfers to this account at year-end.'],
                      ] as [keyof FormValues, string, string][]).map(([fieldName, label, desc]) => (
                        <Controller
                          key={fieldName}
                          name={fieldName}
                          control={control}
                          render={({ field }) => (
                            <ToggleSwitch
                              label={label}
                              description={desc}
                              checked={field.value as boolean}
                              onChange={field.onChange}
                              disabled={fieldsDisabled}
                            />
                          )}
                        />
                      ))}
                    </div>
                  </>
                )}

                {/* ═══ TAB 3: ORGANISATIONAL RULES ══════════════════════════════ */}
                {activeTab === 'org' && (
                  <>
                    <SectionHeading>Mandatory Dimension Rules</SectionHeading>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-3">
                      When enabled, journal lines posting to this account must have the dimension specified.
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {([
                        ['costCenterMandatory', 'Cost Centre Mandatory', 'Journal lines must include a cost centre.'],
                        ['locationMandatory',   'Location Mandatory',    'Journal lines must include a location.'],
                        ['projectMandatory',    'Project Mandatory',     'Journal lines must reference a project.'],
                        ['departmentMandatory', 'Department Mandatory',  'Journal lines must specify a department.'],
                      ] as [keyof FormValues, string, string][]).map(([fieldName, label, desc]) => (
                        <Controller
                          key={fieldName}
                          name={fieldName}
                          control={control}
                          render={({ field }) => (
                            <ToggleSwitch
                              label={label}
                              description={desc}
                              checked={field.value as boolean}
                              onChange={field.onChange}
                              disabled={fieldsDisabled}
                            />
                          )}
                        />
                      ))}
                    </div>

                    {/* ── Permitted locations (GAP-003) ──────────────────────── */}
                    <SectionHeading>Permitted Locations</SectionHeading>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-3">
                      Leave every box clear to let this account be used anywhere. Tick one or more
                      locations to restrict it — the account then disappears from the account
                      picker at other locations, and a transaction that names one is refused.
                    </p>

                    {companyLocations.length === 0 ? (
                      <p className="text-[11px] text-gray-400 italic">
                        No locations are set up for this company yet.
                      </p>
                    ) : (
                      <>
                        <div className="grid grid-cols-2 gap-1.5">
                          {companyLocations.map(loc => {
                            const on = permittedLocations.includes(loc.id);
                            return (
                              <label
                                key={loc.id}
                                className={cn(
                                  'flex items-center gap-2 rounded border px-2.5 py-1.5 text-xs',
                                  fieldsDisabled ? 'cursor-default opacity-70' : 'cursor-pointer',
                                  on
                                    ? 'border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/20'
                                    : 'border-gray-200 dark:border-gray-700',
                                )}
                              >
                                <input
                                  type="checkbox"
                                  checked={on}
                                  disabled={fieldsDisabled}
                                  onChange={() => setPermittedLocations(prev =>
                                    prev.includes(loc.id)
                                      ? prev.filter(id => id !== loc.id)
                                      : [...prev, loc.id])}
                                  className="h-3.5 w-3.5 accent-blue-600"
                                />
                                <span className="font-mono text-[11px] font-semibold text-blue-700 dark:text-blue-400">
                                  {loc.code}
                                </span>
                                <span className="truncate text-gray-700 dark:text-gray-300">{loc.name}</span>
                              </label>
                            );
                          })}
                        </div>

                        <p className="mt-2 text-[11px] font-medium text-gray-600 dark:text-gray-300">
                          {permittedLocations.length === 0
                            ? 'Currently usable at every location.'
                            : `Currently usable at ${permittedLocations.length} of ${companyLocations.length} locations.`}
                        </p>

                        {permittedLocations.length > 0 && !fieldsDisabled && (
                          <button
                            type="button"
                            onClick={() => setPermittedLocations([])}
                            className="mt-1 text-[11px] font-medium text-blue-600 hover:underline dark:text-blue-400"
                          >
                            Remove the restriction
                          </button>
                        )}
                      </>
                    )}
                  </>
                )}

                {/* ═══ TAB 4: POSTING & WORKFLOW ════════════════════════════════ */}
                {activeTab === 'posting' && (
                  <>
                    {/* ── Cheque payee default (GAP-004) ─────────────────────── */}
                    <SectionHeading>Cheque Payee Name</SectionHeading>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
                      The name to print on cheques paid through this account, when it differs from
                      the account name. Leave blank to print the supplier&rsquo;s own name.
                    </p>
                    <input
                      {...register('chequePayeeName')}
                      type="text"
                      disabled={fieldsDisabled}
                      placeholder="e.g. XYZ Supplier (Pvt) Ltd"
                      className={cn(
                        'w-full rounded border px-2.5 py-1.5 text-xs',
                        'border-gray-200 dark:border-gray-700 dark:bg-gray-800',
                        fieldsDisabled && 'bg-gray-50 dark:bg-gray-900 text-gray-500',
                      )}
                    />
                    <p className="mt-1.5 mb-4 text-[11px] text-amber-700 dark:text-amber-400">
                      Suppliers usually share one payables account, so a name set here applies to
                      every supplier paying through it. To name one supplier only, set it on the
                      supplier instead.
                    </p>

                    {/* ── Cheque crossing default (ML-09) ────────────────────── */}
                    <SectionHeading>Default Cheque Crossing</SectionHeading>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
                      How cheques settling through this account are crossed. Leave unset to fall
                      back to the paying bank account&rsquo;s own default.
                    </p>
                    <Controller name="defaultChequeCrossing" control={control}
                      render={({ field }) => (
                        <Combobox
                          value={field.value ?? ''}
                          onChange={v => field.onChange(v || '')}
                          disabled={fieldsDisabled}
                          options={CROSSING_OPTIONS}
                          placeholder="Not set — use the bank account’s default"
                        />
                      )}
                    />
                    <p className="mt-1.5 mb-4 text-[11px] text-amber-700 dark:text-amber-400">
                      A voucher can still cross one cheque differently, but only with the
                      crossing-override permission.
                    </p>

                    <SectionHeading>Posting Permissions</SectionHeading>
                    <div className="grid grid-cols-2 gap-2">
                      {([
                        ['allowManualJournal',  'Allow Manual Journals', 'Allow manual GL journal entries to this account.'],
                        ['allowPaymentPosting', 'Allow Payment Posting', 'Allow payment voucher postings to this account.'],
                        ['allowReceiptPosting', 'Allow Receipt Posting', 'Allow receipt voucher postings to this account.'],
                      ] as [keyof FormValues, string, string][]).map(([fieldName, label, desc]) => (
                        <Controller
                          key={fieldName}
                          name={fieldName}
                          control={control}
                          render={({ field }) => (
                            <ToggleSwitch
                              label={label}
                              description={desc}
                              checked={field.value as boolean}
                              onChange={field.onChange}
                              disabled={fieldsDisabled}
                            />
                          )}
                        />
                      ))}
                    </div>

                    <SectionHeading>Workflow</SectionHeading>
                    <Controller
                      name="approvalRequired"
                      control={control}
                      render={({ field }) => (
                        <ToggleSwitch
                          label="Approval Required"
                          description="Journal entries to this account must be approved before posting."
                          checked={field.value}
                          onChange={field.onChange}
                          disabled={fieldsDisabled}
                        />
                      )}
                    />

                    {approvalReq && (
                      <div className="mt-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-[11px] text-amber-700 dark:text-amber-400">
                        Workflow mapping is configured at the company level. Entries posting to this account will follow the GL approval workflow.
                      </div>
                    )}

                    <SectionHeading>Restrictions</SectionHeading>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <FieldLabel hint="(optional)">Posting Restriction</FieldLabel>
                        <input
                          {...register('postingRestriction')}
                          disabled={fieldsDisabled}
                          placeholder="e.g. PERIOD_LOCK, DR_ONLY"
                          maxLength={100}
                          className={cn(
                            'w-full h-8 px-2.5 rounded-md border text-xs font-mono',
                            'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
                            'border-gray-300 dark:border-gray-600',
                            'focus:outline-none focus:ring-1 focus:ring-brand-500',
                            'disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed',
                          )}
                        />
                      </div>
                      <div>
                        <FieldLabel hint="(optional)">Transaction Restriction</FieldLabel>
                        <input
                          {...register('transactionRestriction')}
                          disabled={fieldsDisabled}
                          placeholder="e.g. JV_ONLY"
                          maxLength={100}
                          className={cn(
                            'w-full h-8 px-2.5 rounded-md border text-xs font-mono',
                            'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
                            'border-gray-300 dark:border-gray-600',
                            'focus:outline-none focus:ring-1 focus:ring-brand-500',
                            'disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed',
                          )}
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* ═══ TAB 5: REPORTING & INTEGRATION ═══════════════════════════ */}
                {activeTab === 'reporting' && (
                  <>
                    <SectionHeading>Report Classification</SectionHeading>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { name: 'reportingGroup'      as const, label: 'Reporting Group',      ph: 'e.g. OPERATING_INCOME' },
                        { name: 'reportingSequence'   as const, label: 'Reporting Sequence',   ph: 'Sort order (number)' },
                        { name: 'dashboardGroup'      as const, label: 'Dashboard Group',      ph: 'e.g. REVENUE, COST' },
                        { name: 'externalMappingCode' as const, label: 'External Mapping Code', ph: 'ERP integration code' },
                      ].map(({ name, label, ph }) => (
                        <div key={name}>
                          <FieldLabel>{label}</FieldLabel>
                          <input
                            {...register(name, { valueAsNumber: name === 'reportingSequence' })}
                            type={name === 'reportingSequence' ? 'number' : 'text'}
                            disabled={fieldsDisabled}
                            placeholder={ph}
                            className={cn(
                              'w-full h-8 px-2.5 rounded-md border text-xs',
                              'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
                              'border-gray-300 dark:border-gray-600',
                              'focus:outline-none focus:ring-1 focus:ring-brand-500',
                              'disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed',
                            )}
                          />
                        </div>
                      ))}
                    </div>

                    <SectionHeading>Regulatory & Consolidation</SectionHeading>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { name: 'consolidationGroup'   as const, label: 'Consolidation Group',   ph: 'Group account code' },
                        { name: 'ifrsMappingCode'      as const, label: 'IFRS Mapping Code',      ph: 'e.g. IAS16, IFRS9' },
                        { name: 'taxReportMappingCode' as const, label: 'Tax Report Mapping',     ph: 'e.g. SR, ES, TX' },
                      ].map(({ name, label, ph }) => (
                        <div key={name}>
                          <FieldLabel>{label}</FieldLabel>
                          <input
                            {...register(name)}
                            disabled={fieldsDisabled}
                            placeholder={ph}
                            className={cn(
                              'w-full h-8 px-2.5 rounded-md border text-xs font-mono',
                              'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
                              'border-gray-300 dark:border-gray-600',
                              'focus:outline-none focus:ring-1 focus:ring-brand-500',
                              'disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed',
                            )}
                          />
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* ═══ TAB 6: AUDIT ═════════════════════════════════════════════ */}
                {activeTab === 'audit' && (
                  <>
                    <SectionHeading>Record Information</SectionHeading>
                    {record ? (
                      <div className="space-y-4">
                        {/* Created / Modified */}
                        <div className="grid grid-cols-2 gap-4">
                          {[
                            { label: 'Created By',    value: record.createdByName ?? record.createdBy },
                            { label: 'Created Date',  value: fmtDate(record.createdDate) },
                            { label: 'Modified By',   value: record.modifiedByName ?? record.modifiedBy },
                            { label: 'Modified Date', value: fmtDate(record.modifiedDate) },
                          ].map(({ label, value }) => (
                            <div key={label} className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700">
                              <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">{label}</div>
                              <div className="text-xs font-medium text-gray-800 dark:text-gray-200">
                                {value ?? '—'}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Account Summary */}
                        <SectionHeading>Account Summary</SectionHeading>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {[
                            { label: 'Account Code',         value: record.code },
                            { label: 'Account Name',         value: record.name },
                            { label: 'Account Type',         value: record.accountType },
                            { label: 'Normal Balance',       value: record.accountNature },
                            { label: 'Financial Statement',  value: record.financialStatementGroup ?? '—' },
                            { label: 'Posting Account',      value: record.isPostingAccount ? 'Yes' : 'No (Header)' },
                            { label: 'Control Account',      value: record.isControlAccount ? 'Yes' : 'No' },
                            { label: 'Approval Required',    value: record.approvalRequired ? 'Yes' : 'No' },
                            { label: 'Status',               value: record.isActive ? 'Active' : 'Inactive' },
                          ].map(({ label, value }) => (
                            <div key={label} className="p-2 rounded bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700">
                              <div className="text-[10px] text-gray-400">{label}</div>
                              <div className="text-xs font-medium text-gray-700 dark:text-gray-300">{value}</div>
                            </div>
                          ))}
                        </div>

                        {/* Approval History placeholder */}
                        <SectionHeading>Approval History</SectionHeading>
                        <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-dashed border-gray-200 dark:border-gray-700 text-center">
                          <p className="text-[11px] text-gray-400">
                            Approval workflow integration coming in Phase 2.
                          </p>
                          <p className="text-[10px] text-gray-300 dark:text-gray-600 mt-1">
                            Status: APPROVED — No approval required for master file changes.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic">
                        Audit information will appear after the record is saved.
                      </p>
                    )}
                  </>
                )}

                {/* ═══ TAB 7: ATTACHMENTS ═══════════════════════════════════════ */}
                {activeTab === 'attachments' && (
                  <>
                    <SectionHeading>Document Attachments</SectionHeading>
                    <div className="p-8 rounded-lg bg-gray-50 dark:bg-gray-800/50 border-2 border-dashed border-gray-200 dark:border-gray-700 text-center">
                      <Paperclip size={28} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Document Management
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Attachment uploads will be available in Phase 2 (Document Management module).
                      </p>
                      <p className="text-[10px] text-gray-300 dark:text-gray-600 mt-2">
                        Supports: PDF, Images, Excel — up to 25MB per file
                      </p>
                    </div>
                  </>
                )}

              </div>
            </form>
          </div>
        </div>
      </div>

      {/* ════ Modals ════ */}

      <MasterSearchModal<AccountListDto>
        isOpen={searchOpen}
        onClose={() => setSearchOpen(false)}
        title="Browse Chart of Accounts"
        columns={SEARCH_COLUMNS}
        fetchData={fetchAccounts}
        onSelect={handleSearchSelect}
      />

      <ConfirmDialog
        isOpen={showNewConfirm !== null}
        variant="info"
        title="Create New Account"
        message={
          <span>
            Account code <strong className="font-mono">{showNewConfirm}</strong> does not exist.
            <br />Do you want to create a new account?
          </span>
        }
        confirmLabel="Yes, Create"
        cancelLabel="No"
        onConfirm={handleConfirmCreateNew}
        onCancel={() => setShowNewConfirm(null)}
      />

      <ConfirmDialog
        isOpen={showSaveConfirm}
        variant="save"
        title={screen.isNew ? 'Save New Account' : 'Save Changes'}
        message={
          screen.isNew
            ? 'Do you want to save this new account?'
            : `Do you want to save changes to account "${record?.name}"?`
        }
        confirmLabel="Yes, Save"
        cancelLabel="No"
        loading={saving}
        onConfirm={executeSave}
        onCancel={() => { setShowSaveConfirm(false); setPendingSave(null); }}
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        variant="delete"
        title="Delete Account"
        message={
          <span>
            Do you want to delete account <strong>{record?.code} — {record?.name}</strong>?
            <br />
            <span className="text-xs text-gray-400 mt-1 block">
              The account will be soft-deleted. Posted journal entries referencing it are unaffected.
            </span>
          </span>
        }
        confirmLabel="Yes, Delete"
        cancelLabel="No"
        loading={saving}
        onConfirm={executeDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      {/* ─── Print Report ─── */}
      <MasterReportPreview
        isOpen={printOpen}
        onClose={() => setPrintOpen(false)}
        title="Chart of Accounts Report"
      >
        <ReportHeader
          reportTitle={record ? 'Account Profile — ' + record.code : 'Chart of Accounts Listing'}
          printedBy={user?.fullName ?? user?.username}
          printedAt={new Date()}
          meta={record ? [
            { label: 'Account Code', value: record.code },
            { label: 'Account Type', value: record.accountType },
            { label: 'Status',       value: record.isActive ? 'Active' : 'Inactive' },
          ] : []}
        />

        {record && (
          <>
            <ReportSection
              title="Account Identification"
              fields={[
                { label: 'Account Code',   value: record.code },
                { label: 'Account Name',   value: record.name, wide: true },
                { label: 'Short Name',     value: record.shortName },
                { label: 'Description',    value: record.description, wide: true },
              ]}
            />
            <ReportSection
              title="Classification"
              fields={[
                { label: 'Account Type',    value: record.accountType },
                { label: 'Normal Balance',  value: record.accountNature },
                { label: 'Category',        value: record.accountCategory },
                { label: 'Subcategory',     value: record.accountSubcategory },
                { label: 'Account Mode',    value: record.isPostingAccount ? 'Posting Account' : 'Header / Group' },
                { label: 'Control Account', value: record.isControlAccount, display: record.isControlAccount ? 'Yes' : 'No' },
                { label: 'Parent Account',  value: record.parentCode ? `${record.parentCode} — ${record.parentName}` : null },
              ]}
            />
            <ReportSection
              title="Financial Controls"
              fields={[
                { label: 'Multi-Currency',       value: record.allowMultiCurrency, display: record.allowMultiCurrency ? 'Yes' : 'No' },
                { label: 'Tax Applicable',       value: record.taxApplicable, display: record.taxApplicable ? 'Yes' : 'No' },
                { label: 'Financial Statement',  value: record.financialStatementGroup },
                { label: 'Cash Flow Category',   value: record.cashFlowCategory },
                { label: 'Revaluation Required', value: record.revaluationRequired, display: record.revaluationRequired ? 'Yes' : 'No' },
              ]}
            />
            <ReportSection
              title="Audit"
              fields={[
                { label: 'Created By',    value: record.createdByName ?? record.createdBy },
                { label: 'Created Date',  value: fmtDate(record.createdDate) },
                { label: 'Modified By',   value: record.modifiedByName ?? record.modifiedBy },
                { label: 'Modified Date', value: fmtDate(record.modifiedDate) },
              ]}
            />
          </>
        )}

        {!record && (
          <div style={{ padding: '12px', color: '#666', fontSize: '12px' }}>
            Load an account record to print its profile, or use Reporting &gt; Chart of Accounts for the full listing.
          </div>
        )}

        <ReportFooter />
      </MasterReportPreview>
    </div>
  );
};
